/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    connectedCallback() {
      this.section = this.closest('.video-section');
      this.init();
    }

    disconnectedCallback() {
      clearTimeout(this.readyTimer);
    }

    init() {
      const url = this.dataset.videoUrl;

      this.startReadyTimer();

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', this.play.bind(this));
      }
    }

    /**
     * Starts the timer that reveals the placeholder again if the player never loads.
     */
    startReadyTimer() {
      clearTimeout(this.readyTimer);
      this.readyTimer = setTimeout(this.markAsFailed.bind(this), Video.readyTimeout);
    }

    /**
     * Flags the player as usable, allowing the cover image to fade out.
     */
    markAsReady() {
      clearTimeout(this.readyTimer);
      if (!this.section) return;

      if (this.retryHandler) {
        this.retryTarget.removeEventListener('click', this.retryHandler);
        this.retryHandler = null;
      }

      this.section.classList.remove('video-section--error');
      this.section.classList.add('video-section--ready');
      this.forceRepaint();
    }

    /**
     * Makes a playing video actually paint.
     *
     * Some browsers leave the video in a composited layer that is never repainted, so
     * it plays while staying fully transparent and the placeholder shows through. Any
     * forced style recalculation brings it back.
     */
    forceRepaint() {
      if (this.type !== 'tag' || !this.player) return;

      this.player.style.opacity = '1';
      // Reading a layout property flushes the pending change straight away.
      void this.player.offsetHeight;
    }

    /**
     * Flags the player as unusable, keeping the cover image and allowing a retry.
     */
    markAsFailed() {
      clearTimeout(this.readyTimer);
      if (!this.section || this.section.classList.contains('video-section--ready')) return;

      this.section.classList.add('video-section--error');

      if (!this.retryHandler) {
        this.retryHandler = this.retry.bind(this);
        this.retryTarget = this.closest('.video-section__media') || this.section;
        this.retryTarget.addEventListener('click', this.retryHandler);
      }
    }

    /**
     * Rebuilds the player after a failed load.
     */
    retry() {
      if (!this.section.classList.contains('video-section--error')) return;

      this.startReadyTimer();

      if (this.type === 'tag') {
        // The click is a user gesture, so playback that autoplay blocked can start now.
        this.pausedByUser = false;
        if (this.player) this.play();
        return;
      }

      this.player = null;
      this.innerHTML = '<div></div>';
      this.init();
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this),
              onError: this.markAsFailed.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');
        this.markAsReady();

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
          this.dispatchEvent(new CustomEvent('on:media:play', {
            detail: {
              player: this.iframe
            },
            bubbles: true
          }));
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');

      if (!this.player) {
        console.warn('video-component: no <video> rendered, check the theme setting', this); // eslint-disable-line
        this.markAsFailed();
        return;
      }

      // Only a playing video actually paints a frame; readiness alone can leave
      // a transparent element over the placeholder.
      this.player.addEventListener('playing', this.markAsReady.bind(this), { once: true });
      this.player.addEventListener('error', this.markAsFailed.bind(this));

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      this.player.addEventListener('play', () => {
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.player
          },
          bubbles: true
        }));
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.closest('.video-section').classList.add('video-section--played');
      } else {
        // 'playing' rather than 'play': the latter fires before any frame exists,
        // which would uncover a still-blank video element.
        this.player.addEventListener('playing', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.addObserver(this.player);

        // Mute the video if a click didn't trigger its load
        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        this.play();
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');
      this.markAsReady();

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.iframe
          },
          bubbles: true
        }));
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        const setWidth = () => {
          // Height can be 0 before layout settles, which would collapse the iframe.
          const height = this.clientHeight || this.closest('.video-section__media')?.clientHeight;
          if (height) this.style.width = `${height * 1.7778}px`;
        };

        setWidth();
        window.addEventListener('resize', window.debounce(setWidth, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
        return;
      }

      const started = this.player.play();

      // Blocked autoplay leaves nothing on screen, so fall back to tap-to-play.
      // A play interrupted by pauseAllMedia rejects too, but that is not a failure.
      if (started && started.catch) {
        started.catch((err) => {
          if (err && err.name === 'NotAllowedError') this.markAsFailed();
        });
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        });
      }, { rootMargin: '0px 0px 200px 0px' });

      observer.observe(el);
    }
  }

  // How long to wait for a player before restoring the placeholder.
  Video.readyTimeout = 8000;

  customElements.define('video-component', Video);
}
